import { motion } from 'framer-motion';
import { Leaf, Award, Users, Clock, Heart, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const values = [
  {
    icon: Leaf,
    title: 'Fraîcheur garantie',
    description: 'Nous sélectionnons chaque fleur avec soin pour garantir une fraîcheur et une qualité exceptionnelles.',
  },
  {
    icon: Heart,
    title: 'Passion artisanale',
    description: "Chaque composition est créée avec amour et minutie par nos fleuristes expérimentés.",
  },
  {
    icon: Award,
    title: 'Excellence suisse',
    description: 'Notre savoir-faire combine tradition florale et créativité contemporaine.',
  },
  {
    icon: MapPin,
    title: 'Ancrage local',
    description: 'Fiers de servir Sion et le Valais depuis plus de 20 ans.',
  },
];

const stats = [
  { value: '20+', label: "Années d'expérience" },
  { value: '193', label: 'Avis Google' },
  { value: '4.9/5', label: 'Note moyenne' },
  { value: '1000+', label: 'Clients satisfaits' },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <span className="text-accent font-accent italic text-lg">Notre histoire</span>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-primary tracking-tight mt-2">
                  À propos d'
                  <span className="text-accent">Alma Fleur</span>
                </h1>
              </div>
              
              <div className="space-y-4 text-muted-foreground leading-relaxed text-lg">
                <p>
                  Nichée au cœur de Sion, <strong className="text-primary">Alma Fleur SA</strong> est 
                  une fleuristerie artisanale qui perpétue la tradition florale suisse depuis plus de deux décennies.
                </p>
                <p>
                  Notre passion pour les fleurs nous guide chaque jour dans la création de bouquets 
                  et compositions qui racontent des histoires, transmettent des émotions et célèbrent 
                  les moments importants de votre vie.
                </p>
                <p>
                  De la sélection méticuleuse des fleurs les plus fraîches à l'assemblage créatif 
                  de chaque composition, nous mettons notre expertise et notre cœur au service de 
                  votre satisfaction.
                </p>
              </div>

              <Link to="/contact" data-testid="about-contact-btn">
                <Button className="rounded-full px-8 py-6 text-lg bg-primary hover:bg-primary/90">
                  Nous contacter
                </Button>
              </Link>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-accent/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80"
                  alt="Intérieur de la boutique Alma Fleur"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="font-serif text-4xl md:text-5xl font-semibold text-primary">
                  {stat.value}
                </div>
                <div className="text-muted-foreground mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block text-accent font-accent italic text-lg mb-4"
            >
              Ce qui nous définit
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-primary tracking-tight"
            >
              Nos Valeurs
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-3xl border border-primary/5 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-primary mb-3">
                  {value.title}
                </h3>
                <p className="text-muted-foreground">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Quote */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.blockquote
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-accent italic text-2xl md:text-3xl lg:text-4xl leading-relaxed"
          >
            "Chaque fleur a une âme, chaque bouquet raconte une histoire. Notre mission est de 
            capturer la beauté de la nature pour sublimer vos moments les plus précieux."
          </motion.blockquote>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-primary-foreground/70"
          >
            — L'équipe Alma Fleur SA
          </motion.p>
        </div>
      </section>
    </div>
  );
}

export { AboutPage };
